<template>
	<div id="chat-container" class="window" :data-current-channel="channel.name" lang="">
		<div
			id="chat"
			:class="{
				'hide-motd': !store.state.settings.motd,
				'time-seconds': store.state.settings.showSeconds,
				'time-12h': store.state.settings.use12hClock,
				'colored-nicks': true, // TODO temporarily fixes themes, to be removed in next major version
			}"
		>
			<div
				:id="'chan-' + channel.id"
				class="chat-view"
				:data-type="channel.type"
				:aria-label="channel.name"
				role="tabpanel"
			>
				<div class="header">
					<button class="header-nav-item" @click="handleKanaliClick">#Канали</button>
					<div class="header-nav-item-privates">
						<button
							ref="privatesButton"
							:class="['header-nav-item', {'has-unread': hasUnreadPrivates}]"
							@click="togglePrivates"
						>
							@ЛИЧНИ
						</button>
						<Teleport to="body">
							<div
								v-if="showPrivates"
								ref="privatesDropdown"
								class="privates-dropdown"
								:style="privatesDropdownStyle"
							>
								<div v-if="privateChannels.length === 0" class="privates-empty">
									Нямаш лични разговори
								</div>
								<div
									v-for="item in privateChannels"
									:key="item.channel.id"
									:class="['privates-dropdown-row', {'has-unread': item.channel.unread}]"
								>
									<button
										class="privates-dropdown-item"
										@click="selectPrivate(item.channel)"
									>
										{{ item.channel.name }}
										<span v-if="item.channel.unread" class="privates-unread-badge">{{
											item.channel.unread
										}}</span>
									</button>
									<button
										class="privates-close-item"
										aria-label="Затвори"
										@click.stop="closePrivate(item.channel)"
									>
										×
									</button>
								</div>
							</div>
						</Teleport>
					</div>
					<router-link
						v-slot:default="{navigate}"
						to="/settings"
						custom
					>
						<button class="header-nav-item header-nav-item-settings" @click="navigate"><span class="header-nav-item-label">Настройки</span></button>
					</router-link>
					<span class="title" :aria-label="'Currently open ' + channel.type">{{
						channel.name
					}}</span>
					<div v-if="channel.editTopic === true" class="topic-container">
						<input
							ref="topicInput"
							:value="channel.topic"
							class="topic-input"
							placeholder="Set channel topic"
							enterkeyhint="done"
							@keyup.enter="saveTopic"
							@keyup.esc="channel.editTopic = false"
						/>
						<span aria-label="Save topic" class="save-topic" @click="saveTopic">
							<span type="button" aria-label="Save topic"></span>
						</span>
					</div>
					<span
						v-else
						:title="plainTopic"
						:class="{topic: true, empty: !channel.topic}"
						@dblclick="editTopic"
						><ParsedMessage
							v-if="channel.topic"
							:network="network"
							:text="channel.topic"
					/></span>
					<MessageSearchForm
						v-if="
							store.state.settings.searchEnabled &&
							['channel', 'query'].includes(channel.type)
						"
						:network="network"
						:channel="channel"
					/>
					<button
						class="mentions"
						aria-label="Open your mentions"
						@click="openMentions"
					/>
					<button
						class="menu"
						aria-label="Open the context menu"
						@click="openContextMenu"
					/>
					<button
						v-if="channel.type === 'channel'"
						class="header-nav-item header-nav-item-users"
						aria-label="Toggle user list"
						@click="store.commit('toggleUserlist')"
					>Потребители</button>
				</div>
				<div v-if="channel.type === 'special'" class="chat-content">
					<div class="chat">
						<div class="messages">
							<div class="msg">
								<component
									:is="specialComponent"
									:network="network"
									:channel="channel"
								/>
							</div>
						</div>
					</div>
				</div>
				<div v-else class="chat-content">
					<button
						:class="[
							'scroll-down tooltipped tooltipped-w tooltipped-no-touch',
							{'scroll-down-shown': !channel.scrolledToBottom},
						]"
						aria-label="Jump to recent messages"
						@click="messageList?.jumpToBottom()"
					>
						<div class="scroll-down-arrow" />
					</button>
					<ChatUserList v-if="channel.type === 'channel'" :channel="channel" />
					<MessageList
						ref="messageList"
						:network="network"
						:channel="channel"
						:focused="focused"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="store.state.currentUserVisibleError"
			id="user-visible-error"
			@click="hideUserVisibleError"
		>
			{{ store.state.currentUserVisibleError }}
		</div>
		<ChatInput :network="network" :channel="channel" />
	</div>
</template>

<script lang="ts">
import socket from "../js/socket";
import eventbus from "../js/eventbus";
import ParsedMessage from "./ParsedMessage.vue";
import MessageList from "./MessageList.vue";
import ChatInput from "./ChatInput.vue";
import ChatUserList from "./ChatUserList.vue";
import SidebarToggle from "./SidebarToggle.vue";
import MessageSearchForm from "./MessageSearchForm.vue";
import ListBans from "./Special/ListBans.vue";
import ListInvites from "./Special/ListInvites.vue";
import ListChannels from "./Special/ListChannels.vue";
import ListIgnored from "./Special/ListIgnored.vue";
import {defineComponent, PropType, ref, computed, watch, nextTick, onMounted, onBeforeUnmount, Component} from "vue";
import type {ClientNetwork, ClientChan} from "../js/types";
import {useStore} from "../js/store";
import {switchToChannel} from "../js/router";
import {useRouter} from "vue-router";
import {SpecialChanType, ChanType} from "../../shared/types/chan";
import parseStyle from "../js/helpers/ircmessageparser/parseStyle";

// ЛАФЧЕ.ORG: module-level (не component state, за да оцелее между смяна на
// каналите) - помни последния РЕАЛЕН канал (не лична), за да #Канали бутонът
// винаги връща директно там, без значение колко лични разговора си отворил
let lastViewedChannel: ClientChan | null = null;

export default defineComponent({
	name: "Chat",
	components: {
		ParsedMessage,
		MessageList,
		ChatInput,
		ChatUserList,
		SidebarToggle,
		MessageSearchForm,
	},
	props: {
		network: {type: Object as PropType<ClientNetwork>, required: true},
		channel: {type: Object as PropType<ClientChan>, required: true},
		focused: Number,
	},
	emits: ["channel-changed"],
	setup(props, {emit}) {
		const store = useStore();

		// ЛАФЧЕ.ORG: "Лични" бутон - падащо меню с всички лични разговори
		// (query type), отделено от обикновените канали в sidebar-а. Teleport-нато
		// извън .header (той има overflow:hidden, изрязваше менюто).
		const router = useRouter();
		const showPrivates = ref(false);
		const privatesButton = ref<HTMLButtonElement | null>(null);
		const privatesDropdown = ref<HTMLElement | null>(null);
		const privatesDropdownStyle = ref({});

		const privateChannels = computed(() => {
			const result: {channel: ClientChan; network: ClientNetwork}[] = [];

			for (const network of store.state.networks) {
				for (const channel of network.channels) {
					if (channel.type === "query") {
						result.push({channel, network});
					}
				}
			}

			return result;
		});

		const togglePrivates = () => {
			showPrivates.value = !showPrivates.value;

			if (showPrivates.value && privatesButton.value) {
				const rect = privatesButton.value.getBoundingClientRect();
				privatesDropdownStyle.value = {
					position: "fixed",
					top: `${rect.bottom + 4}px`,
					left: `${rect.left}px`,
				};
			}
		};

		const selectPrivate = (channel: ClientChan) => {
			switchToChannel(channel);
			showPrivates.value = false;
		};

		watch(
			() => props.channel,
			(newChannel) => {
				if (newChannel.type !== "query") {
					lastViewedChannel = newChannel;
				}
			},
			{immediate: true}
		);

		const handleKanaliClick = () => {
			showPrivates.value = false;

			if (props.channel.type === "query" && lastViewedChannel) {
				switchToChannel(lastViewedChannel);
			} else {
				store.commit("toggleSidebar");
			}
		};

		const hasUnreadPrivates = computed(() => {
			return privateChannels.value.some((item) => item.channel.unread > 0);
		});

		const closePrivate = (channel: ClientChan) => {
			channel.closed = true;
			socket.emit("input", {
				target: Number(channel.id),
				text: "/close",
			});
		};

		const handlePrivatesClickOutside = (e: MouseEvent) => {
			if (!showPrivates.value) {
				return;
			}

			const target = e.target as Node;

			if (
				privatesButton.value &&
				!privatesButton.value.contains(target) &&
				privatesDropdown.value &&
				!privatesDropdown.value.contains(target)
			) {
				showPrivates.value = false;
			}
		};

		const messageList = ref<typeof MessageList>();
		const topicInput = ref<HTMLInputElement | null>(null);

		const plainTopic = computed(() => {
			const topic = props.channel.topic;

			if (!topic) {
				return "";
			}

			return parseStyle(topic)
				.map((fragment) => fragment.text)
				.join("");
		});

		const specialComponent = computed(() => {
			switch (props.channel.special) {
				case SpecialChanType.BANLIST:
					return ListBans as Component;
				case SpecialChanType.INVITELIST:
					return ListInvites as Component;
				case SpecialChanType.CHANNELLIST:
					return ListChannels as Component;
				case SpecialChanType.IGNORELIST:
					return ListIgnored as Component;
			}

			return undefined;
		});

		const channelChanged = () => {
			// Triggered when active channel is set or changed
			emit("channel-changed", props.channel);

			socket.emit("open", props.channel.id);

			if (props.channel.usersOutdated) {
				props.channel.usersOutdated = false;

				socket.emit("names", {
					target: props.channel.id,
				});
			}
		};

		const hideUserVisibleError = () => {
			store.commit("currentUserVisibleError", null);
		};

		const editTopic = () => {
			if (props.channel.type === ChanType.CHANNEL) {
				props.channel.editTopic = true;
			}
		};

		const saveTopic = () => {
			props.channel.editTopic = false;

			if (!topicInput.value) {
				return;
			}

			const newTopic = topicInput.value.value;

			if (props.channel.topic !== newTopic) {
				const target = props.channel.id;
				const text = `/topic ${newTopic}`;
				socket.emit("input", {target, text});
			}
		};

		const openContextMenu = (event: any) => {
			eventbus.emit("contextmenu:channel", {
				event: event,
				channel: props.channel,
				network: props.network,
			});
		};

		const openMentions = (event: any) => {
			eventbus.emit("mentions:toggle", {
				event: event,
			});
		};

		watch(
			() => props.channel,
			() => {
				channelChanged();
			}
		);

		watch(
			() => props.channel.editTopic,
			(newTopic) => {
				if (newTopic) {
					void nextTick(() => {
						topicInput.value?.focus();
					});
				}
			}
		);

		onMounted(() => {
			channelChanged();

			if (props.channel.editTopic) {
				void nextTick(() => {
					topicInput.value?.focus();
				});
			}

			document.addEventListener("click", handlePrivatesClickOutside);
		});

		onBeforeUnmount(() => {
			document.removeEventListener("click", handlePrivatesClickOutside);
			// ЛАФЧЕ.ORG: защитно нулиране - ако падащото меню @ЛИЧНИ е останало
			// отворено (Teleport-нато извън компонента), при навигация към нов
			// канал/личен разговор трябва да се затвори изрично
			showPrivates.value = false;
		});

		return {
			store,
			showPrivates,
			privateChannels,
			togglePrivates,
			selectPrivate,
			privatesButton,
			privatesDropdown,
			privatesDropdownStyle,
			hasUnreadPrivates,
			closePrivate,
			handleKanaliClick,
			messageList,
			topicInput,
			plainTopic,
			specialComponent,
			hideUserVisibleError,
			editTopic,
			saveTopic,
			openContextMenu,
			openMentions,
		};
	},
});
</script>
